/**
 * useGroundStatusSweep
 *
 * Runs in the TrackScreen (main.jsx) on mount and whenever the flights list
 * refreshes. Does a one-time background sweep for pre-flight status changes:
 *
 *  1. DELAYED (time-based) — departure time + 2 h passed, no takeoff
 *  2. IN_TRANSIT (FR24)    — aircraft already airborne when user opens the app
 *  3. READY (FR24 + proximity) — aircraft on ground within 10 km of departure
 *                                airport (when live position alt === 0)
 *
 * Runs at most once per session per flight to avoid hammering the API.
 * All updates are fire-and-forget; cache is invalidated after batch completes.
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { deriveQuoteStatus } from '@/features/track/utils/flightStatusUtils';

const SWEPT_KEY = 'gtj_ground_sweep_ids'; // sessionStorage key
const PRE_FLIGHT = ['POSITIONING', 'READY', 'DELAYED'];
const AIRBORNE_ALT_FT = 1000;
const READY_RADIUS_KM = 10;

// Haversine distance in km (duplicated here to avoid import dependency)
function haversine(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function useGroundStatusSweep(rawFlights) {
  const queryClient = useQueryClient();
  const isRunningRef = useRef(false); // prevents concurrent runs only, not future ones

  useEffect(() => {
    if (isRunningRef.current || !rawFlights?.length) return;
    isRunningRef.current = true;
    runSweep(rawFlights, queryClient).finally(() => {
      isRunningRef.current = false;
    });
  }, [rawFlights, queryClient]); // re-runs on pull-to-refresh; sessionStorage deduplicates per-flight
}

async function runSweep(rawFlights, queryClient) {
  // Only sweep pre-flight flights that haven't been swept this session
  const sweptIds = new Set(
    JSON.parse(sessionStorage.getItem(SWEPT_KEY) || '[]')
  );

  const candidates = rawFlights.filter((f) => {
    const status = (f.flight_status || '').toUpperCase();
    return PRE_FLIGHT.includes(status) && !sweptIds.has(f.id) && f.departure_date;
  });

  if (!candidates.length) return;

  const updates = []; // { flightId, newStatus }
  const now = Date.now();

  // ── 1. Time-based DELAYED check (free, no API call) ──────────────────────
  for (const flight of candidates) {
    const status = (flight.flight_status || '').toUpperCase();
    if (status === 'DELAYED') continue;
    const delayedAt = new Date(flight.departure_date).getTime() + 2 * 60 * 60 * 1000;
    if (now > delayedAt) {
      updates.push({ flight, newStatus: 'DELAYED' });
    }
  }

  // ── 2. FR24 airborne + ground proximity check ─────────────────────────────
  const flightsWithTail = candidates.filter((f) => f.aircraft?.tail_number);

  if (flightsWithTail.length) {
    try {
      const registrations = flightsWithTail
        .map((f) => f.aircraft.tail_number)
        .join(',');

      const response = await apiClient.getLiveFlightPositions(registrations);
      const livePositions = response?.data || [];

      for (const pos of livePositions) {
        const alt = pos.alt ?? 0;
        const reg = (pos.reg || '').toUpperCase();

        const flight = flightsWithTail.find(
          (f) => f.aircraft.tail_number.toUpperCase() === reg
        );
        if (!flight) continue;

        const currentStatus = (flight.flight_status || '').toUpperCase();

        // Airborne → IN_TRANSIT
        if (alt > AIRBORNE_ALT_FT) {
          if (currentStatus !== 'IN_TRANSIT') {
            updates.push({ flight, newStatus: 'IN_TRANSIT' });
          }
          continue;
        }

        // On ground — check READY (within 10 km of departure airport)
        if (alt === 0 && currentStatus === 'POSITIONING' && pos.lat != null && pos.lon != null) {
          const depCode = flight.departure_airport;
          if (depCode) {
            try {
              const airport = await apiClient.getAirportFull(depCode);
              if (airport?.lat != null && airport?.lon != null) {
                const dist = haversine(pos.lat, pos.lon, airport.lat, airport.lon);
                if (dist <= READY_RADIUS_KM) {
                  updates.push({ flight, newStatus: 'READY' });
                }
              }
            } catch {
              // airport lookup failed — skip READY for this flight
            }
          }
        }
      }
    } catch (err) {
      console.error('[GroundSweep] FR24 batch check failed:', err);
    }
  }

  if (!updates.length) {
    // Mark all candidates as swept even if nothing changed
    candidates.forEach((f) => sweptIds.add(f.id));
    sessionStorage.setItem(SWEPT_KEY, JSON.stringify([...sweptIds]));
    return;
  }

  // Deduplicate: if a flight appears in multiple updates, take the highest-priority one
  const priority = { IN_TRANSIT: 3, READY: 2, DELAYED: 1 };
  const deduped = new Map();
  for (const u of updates) {
    const existing = deduped.get(u.flight.id);
    if (!existing || (priority[u.newStatus] ?? 0) > (priority[existing.newStatus] ?? 0)) {
      deduped.set(u.flight.id, u);
    }
  }

  await Promise.allSettled(
    [...deduped.values()].map(async ({ flight, newStatus }) => {
      try {
        console.log(`[GroundSweep] Flight ${flight.id}: ${flight.flight_status} → ${newStatus}`);
        await apiClient.patchFlight(flight.id, { flight_status: newStatus });
        sweptIds.add(flight.id);

        // Cascade to quote status
        const quoteId = flight.quote?.id || flight.quote_id;
        if (quoteId) {
          const newQuoteStatus = await deriveQuoteStatus(quoteId, flight.id, newStatus);
          if (newQuoteStatus) {
            console.log(`[GroundSweep] Quote ${quoteId}: → ${newQuoteStatus}`);
            await apiClient.updateQuote(quoteId, { status: newQuoteStatus });
          }
        }
      } catch (err) {
        console.error(`[GroundSweep] Update failed for ${flight.id}:`, err);
      }
    })
  );

  sessionStorage.setItem(SWEPT_KEY, JSON.stringify([...sweptIds]));
  queryClient.invalidateQueries({ queryKey: ['flights'] });
}
